import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useLinkBillingAccountMutation } from "@/v2-backoffice-client/license/account/view/license.account.link.mutation"
import { evictBillingAccountQueries } from "@/v2-backoffice-client/license/license.utils"

export type LicenseAccountLinkButtonProps = {
  accountId: number
  accountName: string | null | undefined
  /** Null unlinks — the mirror may re-link within the hour if unambiguous. */
  customerId: number | null
  customerName: string | null | undefined
}

// One explicit link decision: link this account to this customer (or, with a
// null customerId, undo the current link). The proc refuses what the mirror
// would refuse — a customer already holding another account, an archived
// account — and its message comes back as the error toast, so the operator
// hears the rule, not a stack trace.
function LicenseAccountLinkButton(props: LicenseAccountLinkButtonProps) {
  const { accountId, accountName, customerId, customerName } = props
  const isUnlink = customerId === null

  const [linkAccount, { loading }] = useLinkBillingAccountMutation({
    update: evictBillingAccountQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: isUnlink ? "Account unlinked" : "Account linked",
        description: isUnlink
          ? `${accountName ?? "The account"} is unmatched again. If its org number matches exactly one customer, the hourly mirror re-links it.`
          : `${accountName ?? "The account"} now bills ${customerName ?? "the selected customer"} — invoices and licenses flow to its panel from the next sync.`,
      })
    },
    onError(mutationError) {
      toast({
        variant: "destructive",
        title: isUnlink ? "Could not unlink" : "Could not link",
        description: mutationError.message,
      })
    },
  })

  return (
    <Button
      type="button"
      size="sm"
      variant={isUnlink ? "outline" : "primary"}
      isLoading={loading}
      data-feature-code={
        isUnlink
          ? "UI_BackofficeBillingAccount_Matches_Unlink"
          : "UI_BackofficeBillingAccount_Matches_Link"
      }
      onClick={() => {
        void linkAccount({ variables: { accountId, customerId } })
      }}
    >
      {isUnlink ? "Unlink" : "Link"}
    </Button>
  )
}

export default LicenseAccountLinkButton
